import { useQuery } from '@tanstack/react-query'
import {
  ActivityIcon,
  CpuIcon,
  DatabaseIcon,
  HardDriveIcon,
  WifiIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  TrendingUpIcon,
  MonitorIcon,
  ZapIcon,
  ThermometerIcon,
  BellIcon,
  HistoryIcon,
  AlertCircleIcon,
  Loader2Icon,
} from 'lucide-react'
import { useState, type ComponentType } from 'react'

import {
  ResponsiveActionRow,
  ResponsivePageHeader,
  ResponsiveTabs,
} from '@nebula/shared/components'
import {
  getCustomerMonitoring,
  getMonitoringStatusColor,
  getMonitoringStatusLabel,
  getMonitoringAlertTypeColor,
  getMonitoringAlertTypeLabel,
  getMonitoringProgressBarColor,
  formatMonitoringRelativeTime,
  type MonitoringVMMetrics,
  type MonitoringAlert,
} from '@nebula/shared/services'
import { useAuthStore } from '@nebula/shared/store'

type MonitoringTab = 'overview' | 'resources' | 'alerts' | 'performance'
type MonitoringTabItem = {
  id: MonitoringTab
  label: string
  icon: ComponentType<{ className?: string }>
}

const MonitoringPage = () => {
  const [activeTab, setActiveTab] = useState<MonitoringTab>('overview')
  const [selectedVM, setSelectedVM] = useState('all')
  const [autoRefresh, setAutoRefresh] = useState(true)

  // Org ativa: mesma fonte do header X-Organization-Id (auth.ts). Sem escopo, a query
  // dispara antes de a org resolver, sai sem o header e o backend responde 200 zerado pela
  // org default (não-EC) — que a tela exibia como medição. Ver issue #726.
  const activeOrgId = useAuthStore((s) => s.activeOrganizationId)

  // Fetch monitoring data from API
  const { data, isLoading, error, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['customer-monitoring', activeOrgId],
    queryFn: getCustomerMonitoring,
    enabled: !!activeOrgId,
    refetchInterval: autoRefresh ? 30_000 : false,
    staleTime: 10_000,
  })

  const lastUpdateTime = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString('pt-BR')
    : '--:--:--'

  const tabs: MonitoringTabItem[] = [
    { id: 'overview', label: 'Visão Geral', icon: MonitorIcon },
    { id: 'resources', label: 'Recursos', icon: ActivityIcon },
    { id: 'alerts', label: 'Alertas', icon: AlertTriangleIcon },
    { id: 'performance', label: 'Performance', icon: TrendingUpIcon },
  ]

  const getStatusBadge = (status: string) => {
    const colorClass = getMonitoringStatusColor(status)
    const label = getMonitoringStatusLabel(status)

    const iconMap: Record<string, typeof CheckCircleIcon> = {
      running: CheckCircleIcon,
      stopped: ClockIcon,
      error: AlertTriangleIcon,
      creating: ClockIcon,
      starting: ClockIcon,
      stopping: ClockIcon,
    }

    const Icon = iconMap[status] || ClockIcon

    return (
      <span
        className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${colorClass}`}
      >
        <Icon className="mr-1 size-3" />
        {label}
      </span>
    )
  }

  const getAlertBadge = (type: string) => {
    const colorClass = getMonitoringAlertTypeColor(type)
    const label = getMonitoringAlertTypeLabel(type)

    return (
      <span
        className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${colorClass}`}
      >
        {label}
      </span>
    )
  }

  // Loading. Precisa do `!activeOrgId` explícito: com `enabled: false` a query não fica em
  // isLoading (v5: isLoading = isPending && isFetching), então sem ele a tela renderizaria
  // métricas antes do primeiro fetch.
  if (isLoading || !activeOrgId) {
    return (
      <div className="mobile-page-padding">
        <ResponsivePageHeader
          title="Monitoramento"
          description="Carregando dados de monitoramento..."
          icon={ActivityIcon}
        />
        <div className="flex h-64 items-center justify-center">
          <Loader2Icon className="size-8 animate-spin text-purple-600" />
        </div>
      </div>
    )
  }

  // Error state (com dado em cache, o erro vira aviso discreto no topo em vez de derrubar
  // os números da tela)
  if (error && !data) {
    return (
      <div className="mobile-page-padding">
        <ResponsivePageHeader title="Monitoramento" icon={ActivityIcon} />
        <div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center">
          <AlertCircleIcon className="mx-auto mb-4 size-12 text-red-400" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">Erro ao carregar dados</h3>
          <p className="mb-4 text-gray-500">Não foi possível carregar os dados de monitoramento.</p>
          <button
            onClick={() => refetch()}
            className="rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    )
  }

  // Sem loading, sem erro e sem dado (ex.: query pausada por falta de rede). Antes dessa
  // guarda a tela caía num fallback inventado — inclusive "uptime 99.99%", exibido como se
  // fosse medição. Ver issue #726.
  if (!data) {
    return (
      <div className="mobile-page-padding">
        <ResponsivePageHeader title="Monitoramento" icon={ActivityIcon} />
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
          <AlertCircleIcon className="mx-auto mb-4 size-12 text-gray-400" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">Dados indisponíveis no momento</h3>
          <p className="mb-4 text-gray-500">
            Nenhuma leitura de monitoramento foi carregada ainda.
          </p>
          <button
            onClick={() => refetch()}
            className="rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    )
  }

  const realtimeMetrics = data.realtime_metrics
  const vmMetrics: MonitoringVMMetrics[] = data.vm_metrics || []
  const alerts: MonitoringAlert[] = data.alerts || []

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Métricas principais */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
        <div className="rounded-lg bg-white p-6 shadow">
          <div className="mb-4 flex items-center justify-between">
            <CpuIcon className="size-8 text-blue-600" />
            <div className="text-right">
              <div className="text-sm text-gray-500">CPU Médio</div>
              <div className="text-2xl font-bold text-gray-900">{realtimeMetrics.cpu}%</div>
            </div>
          </div>
          <div className="h-2 w-full rounded-full bg-gray-200">
            <div
              className={`${getMonitoringProgressBarColor(realtimeMetrics.cpu)} h-2 rounded-full transition-all duration-500`}
              style={{ width: `${Math.min(realtimeMetrics.cpu, 100)}%` }}
            ></div>
          </div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow">
          <div className="mb-4 flex items-center justify-between">
            <DatabaseIcon className="size-8 text-purple-600" />
            <div className="text-right">
              <div className="text-sm text-gray-500">Memória</div>
              <div className="text-2xl font-bold text-gray-900">{realtimeMetrics.memory}%</div>
            </div>
          </div>
          <div className="h-2 w-full rounded-full bg-gray-200">
            <div
              className={`${getMonitoringProgressBarColor(realtimeMetrics.memory)} h-2 rounded-full transition-all duration-500`}
              style={{ width: `${Math.min(realtimeMetrics.memory, 100)}%` }}
            ></div>
          </div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow">
          <div className="mb-4 flex items-center justify-between">
            <HardDriveIcon className="size-8 text-green-600" />
            <div className="text-right">
              <div className="text-sm text-gray-500">Disco</div>
              <div className="text-2xl font-bold text-gray-900">{realtimeMetrics.disk}%</div>
            </div>
          </div>
          <div className="h-2 w-full rounded-full bg-gray-200">
            <div
              className={`${getMonitoringProgressBarColor(realtimeMetrics.disk)} h-2 rounded-full transition-all duration-500`}
              style={{ width: `${Math.min(realtimeMetrics.disk, 100)}%` }}
            ></div>
          </div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow">
          <div className="mb-4 flex items-center justify-between">
            <WifiIcon className="size-8 text-orange-600" />
            <div className="text-right">
              <div className="text-sm text-gray-500">Rede</div>
              <div className="text-2xl font-bold text-gray-900">
                {(realtimeMetrics.network_in + realtimeMetrics.network_out).toFixed(1)} MB/s
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-1 text-xs text-gray-500">
            <TrendingUpIcon className="size-3" />
            <span>In: {realtimeMetrics.network_in.toFixed(1)} MB/s</span>
            <span>Out: {realtimeMetrics.network_out.toFixed(1)} MB/s</span>
          </div>
        </div>
      </div>

      {/* Status das VMs */}
      <div className="rounded-lg bg-white shadow">
        <div className="border-b p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Status das Máquinas Virtuais</h2>
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center space-x-1">
                <div className="size-2 rounded-full bg-green-500"></div>
                <span>{realtimeMetrics.active_vms} Ativas</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="size-2 rounded-full bg-red-500"></div>
                <span>{realtimeMetrics.total_vms - realtimeMetrics.active_vms} Paradas</span>
              </div>
            </div>
          </div>
        </div>
        <div className="p-6">
          {vmMetrics.length === 0 ? (
            <div className="py-8 text-center">
              <MonitorIcon className="mx-auto mb-4 size-12 text-gray-400" />
              <h3 className="mb-2 text-lg font-medium text-gray-900">Nenhuma VM encontrada</h3>
              <p className="text-gray-500">
                Você ainda não possui máquinas virtuais provisionadas.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
              {vmMetrics.map((vm) => (
                <div key={vm.id} className="rounded-lg border border-gray-200 p-4">
                  <div className="mb-3 flex items-start justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">{vm.name}</h3>
                      <p className="text-sm text-gray-500">Uptime: {vm.uptime}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getStatusBadge(vm.status)}
                      {vm.alerts > 0 && (
                        <span className="rounded-full bg-red-100 p-1 text-xs text-red-800">
                          {vm.alerts}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>CPU:</span>
                      <span className="font-medium">{vm.cpu}%</span>
                    </div>
                    <div className="h-1 w-full rounded-full bg-gray-200">
                      <div
                        className={`${getMonitoringProgressBarColor(vm.cpu)} h-1 rounded-full`}
                        style={{ width: `${Math.min(vm.cpu, 100)}%` }}
                      ></div>
                    </div>

                    <div className="flex justify-between text-sm">
                      <span>RAM:</span>
                      <span className="font-medium">{vm.memory}%</span>
                    </div>
                    <div className="h-1 w-full rounded-full bg-gray-200">
                      <div
                        className={`${getMonitoringProgressBarColor(vm.memory)} h-1 rounded-full`}
                        style={{ width: `${Math.min(vm.memory, 100)}%` }}
                      ></div>
                    </div>

                    <div className="flex justify-between text-sm">
                      <span>Disco:</span>
                      <span className="font-medium">{vm.disk}%</span>
                    </div>
                    <div className="h-1 w-full rounded-full bg-gray-200">
                      <div
                        className={`${getMonitoringProgressBarColor(vm.disk)} h-1 rounded-full`}
                        style={{ width: `${Math.min(vm.disk, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )

  const renderResourcesTab = () => (
    <div className="space-y-6">
      {/* Filtros */}
      <div className="rounded-lg bg-white p-6 shadow">
        <ResponsiveActionRow justify="start" className="gap-2 sm:gap-3">
          <select
            value={selectedVM}
            onChange={(e) => setSelectedVM(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-purple-500 sm:w-auto"
          >
            <option value="all">Todas as VMs</option>
            {vmMetrics.map((vm) => (
              <option key={vm.id} value={vm.id}>
                {vm.name}
              </option>
            ))}
          </select>
        </ResponsiveActionRow>
      </div>

      {/* Historical Charts - Coming Soon */}
      <div className="rounded-lg bg-white shadow">
        <div className="border-b p-6">
          <h2 className="text-lg font-semibold text-gray-900">Histórico de Uso de CPU</h2>
        </div>
        <div className="p-8">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
            <HistoryIcon className="mx-auto mb-4 size-12 text-gray-400" />
            <h3 className="mb-2 text-lg font-medium text-gray-900">Histórico de Métricas</h3>
            <p className="text-gray-500">
              Em breve você poderá visualizar o histórico de uso de CPU, memória e disco.
            </p>
          </div>
        </div>
      </div>

      {/* Memory History - Coming Soon */}
      <div className="rounded-lg bg-white shadow">
        <div className="border-b p-6">
          <h2 className="text-lg font-semibold text-gray-900">Histórico de Uso de Memória</h2>
        </div>
        <div className="p-8">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
            <HistoryIcon className="mx-auto mb-4 size-12 text-gray-400" />
            <h3 className="mb-2 text-lg font-medium text-gray-900">Histórico de Memória</h3>
            <p className="text-gray-500">
              Em breve você poderá visualizar o histórico de uso de memória.
            </p>
          </div>
        </div>
      </div>

      {/* Disk I/O - Coming Soon */}
      <div className="rounded-lg bg-white shadow">
        <div className="border-b p-6">
          <h2 className="text-lg font-semibold text-gray-900">I/O de Disco</h2>
        </div>
        <div className="p-8">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
            <HardDriveIcon className="mx-auto mb-4 size-12 text-gray-400" />
            <h3 className="mb-2 text-lg font-medium text-gray-900">Métricas de Disco</h3>
            <p className="text-gray-500">
              Em breve você poderá visualizar as métricas de I/O de disco.
            </p>
          </div>
        </div>
      </div>
    </div>
  )

  const renderAlertsTab = () => (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="rounded-lg bg-white p-6 shadow">
        <ResponsiveActionRow>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">{alerts.length} alertas ativos</span>
          </div>
        </ResponsiveActionRow>
      </div>

      {/* Active Alerts */}
      <div className="rounded-lg bg-white shadow">
        <div className="border-b p-6">
          <h2 className="text-lg font-semibold text-gray-900">Alertas Ativos</h2>
        </div>
        <div className="p-6">
          {alerts.length === 0 ? (
            <div className="py-8 text-center">
              <CheckCircleIcon className="mx-auto mb-4 size-12 text-green-400" />
              <h3 className="mb-2 text-lg font-medium text-gray-900">Nenhum alerta ativo</h3>
              <p className="text-gray-500">Todas as suas VMs estão operando normalmente.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {alerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`rounded-lg border p-4 ${getMonitoringAlertTypeColor(alert.type)}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="mb-2 flex items-center space-x-2">
                        {getAlertBadge(alert.type)}
                        <span className="font-medium text-gray-900">{alert.title}</span>
                      </div>
                      <p className="mb-2 text-sm text-gray-600">{alert.description}</p>
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        {alert.vm_name && <span>VM: {alert.vm_name}</span>}
                        <span>{formatMonitoringRelativeTime(alert.timestamp)}</span>
                        {alert.value > 0 && <span>Valor: {alert.value}%</span>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Custom Alert Rules - Coming Soon */}
      <div className="rounded-lg bg-white shadow">
        <div className="border-b p-6">
          <h2 className="text-lg font-semibold text-gray-900">Regras de Alerta Personalizadas</h2>
        </div>
        <div className="p-8">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
            <BellIcon className="mx-auto mb-4 size-12 text-gray-400" />
            <h3 className="mb-2 text-lg font-medium text-gray-900">Alertas Personalizados</h3>
            <p className="text-gray-500">
              Em breve você poderá criar regras de alerta personalizadas para suas VMs.
            </p>
          </div>
        </div>
      </div>
    </div>
  )

  const renderPerformanceTab = () => (
    <div className="space-y-6">
      {/* Métricas de performance */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <div className="rounded-lg bg-white p-6 shadow">
          <div className="mb-4 flex items-center justify-between">
            <ZapIcon className="size-8 text-yellow-600" />
            <div className="text-right">
              <div className="text-sm text-gray-500">Uptime</div>
              <div className="text-2xl font-bold text-gray-900">{realtimeMetrics.uptime}%</div>
            </div>
          </div>
          <div className="text-sm text-green-600">Disponibilidade do ambiente</div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow">
          <div className="mb-4 flex items-center justify-between">
            <ThermometerIcon className="size-8 text-red-600" />
            <div className="text-right">
              <div className="text-sm text-gray-500">VMs Ativas</div>
              <div className="text-2xl font-bold text-gray-900">
                {realtimeMetrics.active_vms}/{realtimeMetrics.total_vms}
              </div>
            </div>
          </div>
          <div className="text-sm text-gray-500">
            {realtimeMetrics.total_vms > 0
              ? `${Math.round((realtimeMetrics.active_vms / realtimeMetrics.total_vms) * 100)}% em execução`
              : 'Nenhuma VM'}
          </div>
        </div>

        <div className="rounded-lg bg-white p-6 shadow">
          <div className="mb-4 flex items-center justify-between">
            <ActivityIcon className="size-8 text-blue-600" />
            <div className="text-right">
              <div className="text-sm text-gray-500">Alertas</div>
              <div className="text-2xl font-bold text-gray-900">{realtimeMetrics.alerts_count}</div>
            </div>
          </div>
          <div
            className={`text-sm ${realtimeMetrics.alerts_count > 0 ? 'text-yellow-600' : 'text-green-600'}`}
          >
            {realtimeMetrics.alerts_count > 0 ? 'Alertas ativos' : 'Sistema saudável'}
          </div>
        </div>
      </div>

      {/* Performance Score - Coming Soon */}
      <div className="rounded-lg bg-white shadow">
        <div className="border-b p-6">
          <h2 className="text-lg font-semibold text-gray-900">Score de Performance</h2>
        </div>
        <div className="p-8">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
            <TrendingUpIcon className="mx-auto mb-4 size-12 text-gray-400" />
            <h3 className="mb-2 text-lg font-medium text-gray-900">Análise de Performance</h3>
            <p className="text-gray-500">
              Em breve você poderá visualizar uma análise detalhada de performance e recomendações
              de otimização.
            </p>
          </div>
        </div>
      </div>

      {/* Current Metrics Summary */}
      <div className="rounded-lg bg-white shadow">
        <div className="border-b p-6">
          <h2 className="text-lg font-semibold text-gray-900">Resumo de Métricas Atuais</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <h3 className="mb-4 font-medium text-gray-900">Uso de Recursos</h3>
              <div className="space-y-4">
                <div>
                  <div className="mb-1 flex justify-between text-sm">
                    <span>CPU Médio</span>
                    <span className="font-medium">{realtimeMetrics.cpu}%</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-gray-200">
                    <div
                      className={`${getMonitoringProgressBarColor(realtimeMetrics.cpu)} h-2 rounded-full`}
                      style={{ width: `${Math.min(realtimeMetrics.cpu, 100)}%` }}
                    ></div>
                  </div>
                </div>

                <div>
                  <div className="mb-1 flex justify-between text-sm">
                    <span>Memória Média</span>
                    <span className="font-medium">{realtimeMetrics.memory}%</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-gray-200">
                    <div
                      className={`${getMonitoringProgressBarColor(realtimeMetrics.memory)} h-2 rounded-full`}
                      style={{ width: `${Math.min(realtimeMetrics.memory, 100)}%` }}
                    ></div>
                  </div>
                </div>

                <div>
                  <div className="mb-1 flex justify-between text-sm">
                    <span>Disco Médio</span>
                    <span className="font-medium">{realtimeMetrics.disk}%</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-gray-200">
                    <div
                      className={`${getMonitoringProgressBarColor(realtimeMetrics.disk)} h-2 rounded-full`}
                      style={{ width: `${Math.min(realtimeMetrics.disk, 100)}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="mb-4 font-medium text-gray-900">Status do Ambiente</h3>
              <div className="space-y-3">
                {realtimeMetrics.alerts_count === 0 ? (
                  <div className="flex items-start space-x-3 rounded-lg border border-green-200 bg-green-50 p-3">
                    <CheckCircleIcon className="mt-0.5 size-5 text-green-600" />
                    <div>
                      <h4 className="text-sm font-medium text-green-800">Sistema Saudável</h4>
                      <p className="text-sm text-green-700">
                        Todas as VMs estão operando normalmente
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start space-x-3 rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                    <AlertTriangleIcon className="mt-0.5 size-5 text-yellow-600" />
                    <div>
                      <h4 className="text-sm font-medium text-yellow-800">Atenção Necessária</h4>
                      <p className="text-sm text-yellow-700">
                        {realtimeMetrics.alerts_count} alerta(s) ativo(s) requerem atenção
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex items-start space-x-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
                  <MonitorIcon className="mt-0.5 size-5 text-blue-600" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-800">VMs Monitoradas</h4>
                    <p className="text-sm text-blue-700">
                      {realtimeMetrics.total_vms} VMs no total, {realtimeMetrics.active_vms} em
                      execução
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className="mobile-page-padding">
      <ResponsivePageHeader
        title="Monitoramento"
        description="Acompanhe métricas e performance das suas VMs em tempo real"
        icon={ActivityIcon}
        actions={
          <ResponsiveActionRow justify="end" className="w-full sm:w-auto">
            <div className="flex flex-col items-end gap-1">
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-medium ${
                  autoRefresh ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                }`}
              >
                Auto-refresh {autoRefresh ? 'ligado' : 'desligado'}
              </button>
              <div className="text-[10px] text-gray-500">Última atualização: {lastUpdateTime}</div>
            </div>
          </ResponsiveActionRow>
        }
      />

      {error && (
        <div className="mb-6 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
          <AlertTriangleIcon className="size-4 shrink-0" />
          <span>
            Não foi possível atualizar as métricas. Exibindo a última leitura ({lastUpdateTime}).
          </span>
          <button onClick={() => refetch()} className="font-medium underline">
            Tentar novamente
          </button>
        </div>
      )}

      <div className="mb-6">
        <ResponsiveTabs
          tabs={tabs}
          value={activeTab}
          onChange={setActiveTab}
          ariaLabel="Abas de monitoramento"
        />
      </div>

      {/* Content */}
      <div>
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'resources' && renderResourcesTab()}
        {activeTab === 'alerts' && renderAlertsTab()}
        {activeTab === 'performance' && renderPerformanceTab()}
      </div>
    </div>
  )
}

export default MonitoringPage
